import React, { useEffect, useState } from "react";
import { Banknote, CreditCard, Landmark } from "lucide-react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const METHOD_ICON = { Contanti: Banknote, Carta: CreditCard, Bonifico: Landmark };
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Reports() {
  const [data, setData] = useState({ stores: [] });
  const [month, setMonth] = useState(thisMonth());
  useEffect(() => {
    api.get("/reports", { params: { month: month || undefined } }).then((r) => setData(r.data));
  }, [month]);

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Report</h1>
      <div className="grid gap-6 md:grid-cols-2">
        {data.stores.map((s) => (
          <Card key={s.store_id} className="p-6" data-testid={`report-${s.store_id}`}>
            <h2 className="font-heading text-lg font-bold">{s.store_name}</h2>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <Metric label="Riparazioni" value={s.repairs} />
              <Metric label="Completate" value={s.completed} />
              <Metric label="Annullate" value={s.cancelled} />
              <Metric label="Preventivi" value={s.quotes} />
              <Metric label="Accettazione" value={`${s.acceptance_rate}%`} />
              <Metric label="Fatturato" value={fmtEuro(s.revenue)} />
            </div>
          </Card>
        ))}
        {data.stores.length === 0 && <Card className="p-12 text-center text-muted-foreground">Nessun dato disponibile.</Card>}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold">Pagamenti a saldo per metodo</h2>
          <p className="text-sm text-muted-foreground">Solo i pagamenti registrati come saldo</p>
        </div>
        <Input type="month" data-testid="report-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" />
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {data.stores.map((s) => <SaldoCard key={s.store_id} store={s} />)}
      </div>
    </div>
  );
}

function SaldoCard({ store }) {
  const rows = store.saldo_payments || [];
  const total = rows.reduce((a, r) => a + r.amount, 0);
  const count = rows.reduce((a, r) => a + r.count, 0);
  return (
    <Card className="p-6" data-testid={`saldo-${store.store_id}`}>
      <h3 className="font-heading font-bold">{store.store_name}</h3>
      <div className="mt-4 space-y-2">
        {rows.map((r) => {
          const Icon = METHOD_ICON[r.method] || Banknote;
          return (
            <div key={r.method} className="flex items-center justify-between rounded-lg bg-secondary/50 px-4 py-3"
              data-testid={`saldo-${store.store_id}-${r.method.toLowerCase()}`}>
              <span className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-primary" /> {r.method}</span>
              <span className="text-sm"><b>{r.count}</b> pagamenti · <b>{fmtEuro(r.amount)}</b></span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex justify-between border-t border-border pt-3 text-sm font-bold">
        <span>Totale saldi ({count})</span><span>{fmtEuro(total)}</span>
      </div>
    </Card>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg bg-secondary/50 p-3">
      <div className="font-heading text-xl font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
